import api from './api'
import {
  EtapaFormulario,
  FormularioReserva,
  ItemContextoIa,
  PendenciaFormulario,
  SalvarFormularioRequest,
} from '@/types/formulario'

const BASE_URL = '/api/v1/oportunidades'

/**
 * Formulário dinâmico da reserva (Onda 4 / L5–L6) — `FormularioReservaController` do
 * módulo de oportunidades. Service PRÓPRIO (dono DEV-L); o admin (DEV-K) acrescenta os
 * endpoints de `/formularios/produto/{produtoId}` aqui, sem mexer nestes.
 *
 * Escopo: o backend carrega a reserva com `carregar(id, tenantScopeId, consultorOwnerId)` —
 * reserva de outro consultor volta como "não encontrada" (fail-closed), como no resto do
 * workspace. Autor de cada resposta = `sub` do token, nunca o corpo.
 */
export const formularioService = {
  /** Formulário da etapa (versão vinculada ou a vigente oferecida), respostas e pendências. */
  obter: async (reservaId: string, etapa: EtapaFormulario): Promise<FormularioReserva> => {
    return (await api.get<FormularioReserva>(
      `${BASE_URL}/${reservaId}/formulario?etapa=${etapa}`
    )) as unknown as FormularioReserva
  },

  /**
   * Grava as respostas da etapa (substitui o conjunto). O servidor valida o formato
   * (400 `FORMULARIO_RESPOSTA_INVALIDA`), descarta o que está oculto por condição, fixa a
   * versão quando é o caso e devolve o estado novo com as pendências recalculadas.
   */
  salvar: async (
    reservaId: string,
    etapa: EtapaFormulario,
    corpo: SalvarFormularioRequest
  ): Promise<FormularioReserva> => {
    return (await api.put<FormularioReserva>(
      `${BASE_URL}/${reservaId}/formulario?etapa=${etapa}`,
      corpo
    )) as unknown as FormularioReserva
  },

  /**
   * Contexto do formulário para a Defesa com IA: só campos marcados `enviarParaIa` das
   * versões vinculadas, NUNCA CPF (o filtro é do servidor; a tela só junta o texto).
   */
  contextoIa: async (reservaId: string): Promise<ItemContextoIa[]> => {
    const r = (await api.get<ItemContextoIa[]>(
      `${BASE_URL}/${reservaId}/formulario/contexto-ia`
    )) as unknown as ItemContextoIa[] | null
    return Array.isArray(r) ? r : []
  },
}

// ─── Erros do contrato ──────────────────────────────────────────────────────

interface ErroApi {
  status?: number
  message?: string
  data?: { errorCode?: string; message?: string; data?: unknown }
}

/** `errorCode` do envelope de erro do backend (`ApiResponse.errorCode`). */
export const codigoErroFormulario = (e: unknown): string | undefined =>
  (e as ErroApi | null | undefined)?.data?.errorCode

/**
 * Pendências que acompanham um 422 `FORMULARIO_INCOMPLETO` (na transição para ENVIADA ou no
 * encaminhamento). O envelope traz a lista em `data` — aceita também `data.pendencias`.
 * Qualquer outro erro → lista vazia.
 */
export const pendenciasDoErro = (e: unknown): PendenciaFormulario[] => {
  if (codigoErroFormulario(e) !== 'FORMULARIO_INCOMPLETO') return []
  const dados = (e as ErroApi).data?.data
  if (Array.isArray(dados)) return dados as PendenciaFormulario[]
  const aninhado = (dados as { pendencias?: unknown } | null | undefined)?.pendencias
  return Array.isArray(aninhado) ? (aninhado as PendenciaFormulario[]) : []
}

const MENSAGEM_ERRO_FORMULARIO: Record<string, string> = {
  FORMULARIO_VERSAO_DIVERGENTE:
    'O formulário desta reserva mudou de versão enquanto você editava. Recarregue para continuar.',
  FORMULARIO_INCOMPLETO: 'Ainda faltam dados solicitados pelo produto.',
  // Contrato real do DEV-J (integração Onda 4): 400 fora da janela / etapa sem formulário.
  FORMULARIO_NAO_EDITAVEL:
    'Os dados do produto não podem mais ser alterados nesta etapa — depois da janela, só por pendência.',
  FORMULARIO_INEXISTENTE: 'Este produto não solicita dados nesta etapa.',
}

/**
 * 400 `FORMULARIO_RESPOSTA_INVALIDA`: `data` = `[{grupo, indice, campo, mensagem}]`
 * (`ErroRespostaFormulario`). Junta as mensagens por campo; sem lista, a do envelope.
 */
const mensagemRespostaInvalida = (e: unknown): string | null => {
  const lista = (e as ErroApi).data?.data
  if (!Array.isArray(lista) || lista.length === 0) return null
  const itens = (lista as { campo?: string | null; mensagem?: string | null }[])
    .map((x) => (x.campo ? `${x.campo}: ${x.mensagem ?? 'valor inválido'}` : x.mensagem))
    .filter((x): x is string => !!x)
  return itens.length ? `Revise os dados do produto — ${itens.slice(0, 3).join('; ')}.` : null
}

/** Mensagem em português para erros do formulário (sem código bruto). */
export const mensagemErroFormulario = (e: unknown, fallback: string): string => {
  const codigo = codigoErroFormulario(e)
  if (codigo && MENSAGEM_ERRO_FORMULARIO[codigo]) return MENSAGEM_ERRO_FORMULARIO[codigo]
  if (codigo === 'FORMULARIO_RESPOSTA_INVALIDA') {
    const m = mensagemRespostaInvalida(e)
    if (m) return m
  }
  const msg = (e as ErroApi | null | undefined)?.message
  return typeof msg === 'string' && msg ? msg : fallback
}
